/**
 * ClaimPage -- public /claim/:code landing for referrers ready to cash out.
 *
 * Flow:
 *   1. Read :code from pathname (/claim/ALEX2026)
 *   2. Fetch their current balance + stored payout method via /api/referrals/balance
 *   3. Show balance and (if claimable > 0) a single-screen payout form
 *   4. POST /api/referrals/claim -- server stamps claim_requested_at; admin
 *      sees it in the payouts dashboard and settles the payment.
 *
 * No authentication. The unguessable referrer code IS the auth token, the
 * same pattern used by Stripe payment links or Calendly slots.
 */

import { useEffect, useState } from "react";

interface Balance {
  code: string;
  name: string;
  payoutMethod: string | null;
  payoutHandle: string | null;
  claimableUsd: number;
  pendingUsd: number;
  lifetimePaidCount: number;
  lifetimePaidUsd: number;
  lifetimeReferredCount: number;
}

const METHOD_LABELS: Record<string, string> = {
  zelle: "Zelle",
  venmo: "Venmo",
  paypal: "PayPal",
  cashapp: "Cash App",
  other: "Other",
};

const HANDLE_HINT: Record<string, string> = {
  zelle: "Email or phone number linked to your Zelle",
  venmo: "Your @username or phone",
  paypal: "Email address linked to PayPal",
  cashapp: "Your $cashtag",
  other: "Describe how we should send",
};

export default function ClaimPage() {
  const pathMatch = window.location.pathname.match(/^\/claim\/([A-Za-z0-9_-]{4,40})$/);
  const code = pathMatch ? pathMatch[1] : null;

  const [balance, setBalance] = useState<Balance | null>(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  const [method, setMethod] = useState<string>("zelle");
  const [handle, setHandle] = useState<string>("");
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!code) {
      setLoading(false);
      setNotFound(true);
      return;
    }
    (async () => {
      try {
        const r = await fetch(`/api/referrals/balance?code=${encodeURIComponent(code)}`);
        if (r.status === 404) {
          setNotFound(true);
        } else if (r.ok) {
          const b = (await r.json()) as Balance;
          setBalance(b);
          if (b.payoutMethod) setMethod(b.payoutMethod);
          if (b.payoutHandle) setHandle(b.payoutHandle);
        } else {
          setError("Couldn't load your balance. Please refresh.");
        }
      } catch {
        setError("Network error. Please refresh.");
      } finally {
        setLoading(false);
      }
    })();
  }, [code]);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!code) return;
    setSubmitting(true);
    setError(null);
    try {
      const r = await fetch("/api/referrals/claim", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ code, payoutMethod: method, payoutHandle: handle.trim() }),
      });
      if (!r.ok) {
        const j = (await r.json().catch(() => ({}))) as { error?: string };
        throw new Error(j.error || "Submit failed");
      }
      setSubmitted(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Submit failed");
    } finally {
      setSubmitting(false);
    }
  }

  if (loading) {
    return <Centered><p className="text-slate-600">Loading...</p></Centered>;
  }

  if (notFound || !code) {
    return (
      <Centered>
        <h1 className="text-2xl font-bold mb-2">Link not found</h1>
        <p className="text-slate-600">This claim link doesn't match any referrer. Check the URL and try again.</p>
      </Centered>
    );
  }

  if (submitted) {
    return (
      <Centered>
        <h1 className="text-2xl font-bold mb-2">Thanks, {balance?.name.split(" ")[0] || "there"}. We'll be in touch</h1>
        <p className="text-slate-600 mb-6">
          We'll send <strong>${balance?.claimableUsd.toFixed(2)}</strong> to your {METHOD_LABELS[method]} within 1&ndash;2 business days.
          You'll get a receipt email with the confirmation number.
        </p>
        <p className="text-sm text-slate-500">
          New referrals will keep stacking. Just come back here any time to claim again.
        </p>
      </Centered>
    );
  }

  if (!balance) {
    return <Centered><p className="text-red-700">{error || "Something went wrong."}</p></Centered>;
  }

  const hasClaimable = balance.claimableUsd > 0;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 py-12 px-4">
      <div className="max-w-md mx-auto">
        <div className="bg-white rounded-lg shadow-sm border border-slate-200 p-8">
          <p className="text-sm text-slate-500 uppercase tracking-wide mb-2">Referrer</p>
          <h1 className="text-2xl font-bold mb-6">{balance.name}</h1>

          <div className="bg-cream-50 border border-slate-200 rounded-lg p-5 mb-6" style={{ background: "#f7f8f1" }}>
            <p className="text-xs text-slate-500 uppercase tracking-wide">Available to claim</p>
            <p className="text-4xl font-bold my-1">${balance.claimableUsd.toFixed(2)}</p>
            {balance.pendingUsd > 0 && (
              <p className="text-sm text-slate-600 mt-2">
                Plus <strong>${balance.pendingUsd.toFixed(2)}</strong> still in the 30-day holding period.
              </p>
            )}
          </div>

          {!hasClaimable ? (
            <div className="text-slate-600">
              <p className="mb-3">You don't have any commissions ready to claim right now.</p>
              {balance.pendingUsd > 0 && (
                <p>Your pending ${balance.pendingUsd.toFixed(2)} will unlock as each friend's 30-day refund window passes. We'll email you the moment any balance becomes claimable.</p>
              )}
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-900 mb-1">Send it via</label>
                <select
                  value={method}
                  onChange={(e) => setMethod(e.target.value)}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg"
                >
                  {Object.entries(METHOD_LABELS).map(([v, l]) => (
                    <option key={v} value={v}>{l}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-900 mb-1">Your {METHOD_LABELS[method]} {method === "cashapp" ? "$cashtag" : "handle"}</label>
                <input
                  type="text"
                  value={handle}
                  onChange={(e) => setHandle(e.target.value)}
                  placeholder={HANDLE_HINT[method]}
                  required
                  minLength={3}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg"
                />
              </div>

              {error && <p className="text-sm text-red-700">{error}</p>}

              <button
                type="submit"
                disabled={submitting}
                className="w-full py-3 bg-emerald-600 text-white rounded-lg font-semibold disabled:opacity-50"
              >
                {submitting ? "Submitting..." : `Claim $${balance.claimableUsd.toFixed(2)}`}
              </button>

              <p className="text-xs text-slate-500 text-center">
                We'll send the full balance to the handle above within 1&ndash;2 business days.
              </p>
            </form>
          )}

          <div className="mt-8 pt-6 border-t border-slate-200 text-sm text-slate-500">
            Lifetime: <strong>{balance.lifetimeReferredCount}</strong> friends referred &middot; <strong>${balance.lifetimePaidUsd.toFixed(2)}</strong> paid
          </div>
        </div>
      </div>
    </div>
  );
}

function Centered({ children }: { children: React.ReactNode }) {
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center px-4">
      <div className="max-w-md w-full bg-white rounded-lg p-8 text-center shadow-sm border border-slate-200">
        {children}
      </div>
    </div>
  );
}
